import { CometChat } from '@cometchat/chat-sdk-javascript';
import { useCallback, useId, useState } from 'react';
import ChatDemo from '../../ChatDemo';
import { meetingGuid } from '../../group-tags';
import { MdIcon } from '../Icon/MdIcon';
import { Avatar, Icon } from '../../components';
import type { CallRecord, Recording } from '../../types/history';
import { Dialog } from '../Dialog/Dialog';
import './CallDetails.css';
import VideoPlayerDialog from './VideoPlayerDialog';

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationMinutes(mins: number): string {
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Transcripts are often only seconds long, so keep the second-level precision
// that formatDurationMinutes() rounds away.
function formatDurationSeconds(secs: number): string {
  if (secs < 60) return `${Math.round(secs)}s`;
  return formatDurationMinutes(secs / 60);
}

// The list API still hands back the legacy recordings host for some assets.
function assetUrl(url: string): string {
  return url.replace(
    'recordings-us.cometchat-staging.com',
    'recordings.rtcv5-us.cometchat-staging.com',
  );
}

function getStatusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('end')) return 'detail-status-badge--ended';
  if (s.includes('ongoing') || s.includes('active'))
    return 'detail-status-badge--ongoing';
  if (s.includes('cancel') || s.includes('miss'))
    return 'detail-status-badge--cancelled';
  return 'detail-status-badge--default';
}

interface CallDetailsProps {
  call: CallRecord;
  onClose: () => void;
}

function CallDetails({ call, onClose }: CallDetailsProps) {
  const [downloadingRids, setDownloadingRids] = useState<Map<string, number>>(
    new Map(),
  );
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const titleId = useId();
  // The meeting's chat group (guid = session id, created when the call was
  // joined). Opened in its own dialog; joined first if this user wasn't in it.
  const [chatGroup, setChatGroup] = useState<CometChat.Group | null>(null);
  const [chatState, setChatState] = useState<'idle' | 'loading' | 'missing'>(
    'idle',
  );
  const openChat = useCallback(async () => {
    setChatState('loading');
    try {
      let group = await CometChat.getGroup(meetingGuid(call.sessionId));
      if (!group.getHasJoined()) {
        group = await CometChat.joinGroup(
          group.getGuid(),
          group.getType() as CometChat.GroupType,
          '',
        );
      }
      setChatGroup(group);
      setChatState('idle');
    } catch (e) {
      console.warn('No chat group for this meeting:', e);
      setChatState('missing');
    }
  }, [call.sessionId]);

  const handleDownload = useCallback(async (rec: Recording) => {
    setDownloadingRids(prev => new Map(prev).set(rec.rid, 0));
    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', assetUrl(rec.recording_url), true);
        xhr.responseType = 'blob';
        xhr.onprogress = e => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setDownloadingRids(prev => new Map(prev).set(rec.rid, pct));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response as Blob);
          } else {
            reject(new Error(`HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording-${rec.rid}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingRids(prev => {
        const next = new Map(prev);
        next.delete(rec.rid);
        return next;
      });
    }
  }, []);

  // The same panel as before, now shown as a popup over the list: the dialog
  // supplies the scrim, focus handling and Escape, the panel keeps its look.
  return (
    <Dialog bare labelledBy={titleId} onClose={onClose}>
      <div className="history-detail-panel">
        <div className="detail-header">
          <h2 className="detail-header__title" id={titleId}>
            Call Details
          </h2>
          <button
            className="detail-close-btn"
            onClick={onClose}
            aria-label="Close detail panel">
            <Icon name="close" size={18} fill="currentColor" />
          </button>
        </div>

        <div className="detail-body">
          {/* Overview */}
          <div className="detail-section">
            <h3 className="detail-section__title">Overview</h3>
            <div className="detail-info-grid">
              <div className="detail-info-card">
                <span className="detail-info-card__label">Status</span>
                <span
                  className={`detail-status-badge ${getStatusBadgeClass(call.status || '')}`}>
                  <span className="detail-status-dot" />
                  {call.status || 'Unknown'}
                </span>
              </div>
              <div className="detail-info-card">
                <span className="detail-info-card__label">Duration</span>
                <span className="detail-info-card__value">
                  {call.totalDuration}
                </span>
              </div>
              <div className="detail-info-card">
                <span className="detail-info-card__label">Started</span>
                <span className="detail-info-card__value">
                  {formatDate(call.startedAt)} {formatTime(call.startedAt)}
                </span>
              </div>
              <div className="detail-info-card">
                <span className="detail-info-card__label">Ended</span>
                <span className="detail-info-card__value">
                  {call.endedAt
                    ? `${formatDate(call.endedAt)} ${formatTime(call.endedAt)}`
                    : '—'}
                </span>
              </div>
              <div className="detail-info-card">
                <span className="detail-info-card__label">Mode</span>
                <span className="detail-info-card__value">
                  {call.mode || '—'}
                </span>
              </div>
              <div className="detail-info-card">
                <span className="detail-info-card__label">Type</span>
                <span className="detail-info-card__value">
                  {call.type || '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Session ID */}
          <div className="detail-section">
            <h3 className="detail-section__title">Session ID</h3>
            <div className="detail-session-id">
              {call.sessionId.split('.').pop()}
            </div>
          </div>

          {/* Meeting chat */}
          <div className="detail-section">
            <h3 className="detail-section__title">Meeting chat</h3>
            <div className="detail-chat-row">
              <button
                type="button"
                className="md-tonal-button"
                onClick={openChat}
                disabled={chatState === 'loading'}>
                <MdIcon name="forum" size={18} />
                {chatState === 'loading' ? 'Opening…' : 'Open meeting chat'}
              </button>
              {chatState === 'missing' && (
                <span className="detail-no-recordings">
                  This meeting has no chat group.
                </span>
              )}
            </div>
          </div>

          {/* Participants */}
          <div className="detail-section">
            <h3 className="detail-section__title">
              Participants ({call.totalParticipants})
            </h3>
            <div className="detail-participant-list">
              {call.participants.map((p, i) => (
                <div key={`${p.uid}-${i}`} className="detail-participant-row">
                  <div className="detail-participant-avatar">
                    <Avatar name={p.name} imageUrl={p.avatar} size={36} />
                  </div>
                  <div className="detail-participant-info">
                    <div className="detail-participant-name">{p.name}</div>
                    <div className="detail-participant-meta">
                      {p.uid}
                      {p.state ? ` · ${p.state}` : ''}
                    </div>
                  </div>
                  <div className="detail-participant-duration">
                    {formatDurationMinutes(p.totalDurationInMinutes)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recordings */}
          <div className="detail-section">
            <h3 className="detail-section__title">Recordings</h3>
            {call.recordings && call.recordings.length > 0 ? (
              <div className="detail-recording-list">
                {call.recordings.map(rec => {
                  const progress = downloadingRids.get(rec.rid);
                  const isDownloading = progress !== undefined;
                  return (
                    <div key={rec.rid} className="detail-recording-row">
                      <div className="detail-recording-icon">
                        <Icon
                          name="video-fill"
                          size={18}
                          fill="var(--cometchat-calls-primary-color)"
                        />
                      </div>
                      <div className="detail-recording-info">
                        <div className="detail-recording-name">Recording</div>
                        <div className="detail-recording-meta">
                          {rec.duration
                            ? formatDurationMinutes(rec.duration / 60)
                            : 'Unknown duration'}
                          {rec.startTime
                            ? ` · ${formatTime(rec.startTime)}`
                            : ''}
                        </div>
                      </div>
                      <button
                        className="detail-play-btn"
                        onClick={() =>
                          setPlayingUrl(assetUrl(rec.recording_url))
                        }
                        aria-label="Play recording">
                        <Icon
                          name="play-fill"
                          size={18}
                          fill="var(--cometchat-calls-primary-color)"
                        />
                      </button>
                      <button
                        className="detail-download-btn"
                        onClick={() => handleDownload(rec)}
                        disabled={isDownloading}
                        aria-label="Download recording">
                        {isDownloading ? (
                          <svg
                            className="detail-progress-ring"
                            width="22"
                            height="22"
                            viewBox="0 0 22 22">
                            <circle
                              className="detail-progress-ring__bg"
                              cx="11"
                              cy="11"
                              r="9"
                              fill="none"
                              strokeWidth="2"
                            />
                            <circle
                              className="detail-progress-ring__fg"
                              cx="11"
                              cy="11"
                              r="9"
                              fill="none"
                              strokeWidth="2"
                              strokeDasharray={`${2 * Math.PI * 9}`}
                              strokeDashoffset={`${2 * Math.PI * 9 * (1 - (progress ?? 0) / 100)}`}
                              strokeLinecap="round"
                            />
                          </svg>
                        ) : (
                          <Icon
                            name="download"
                            size={18}
                            fill="var(--cometchat-calls-primary-color)"
                          />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="detail-no-recordings">
                No recordings for this session
              </span>
            )}
          </div>

          {/* Transcripts */}
          <div className="detail-section">
            <h3 className="detail-section__title">Transcripts</h3>
            {call.transcriptions && call.transcriptions.length > 0 ? (
              <div className="detail-recording-list">
                {call.transcriptions.map((t, i) => {
                  const duration =
                    t.startTime && t.endTime ? t.endTime - t.startTime : 0;
                  return (
                    <div
                      key={t.tid ?? `transcript-${i}`}
                      className="detail-recording-row">
                      <div className="detail-recording-icon">
                        <Icon
                          name="wave"
                          size={18}
                          fill="var(--cometchat-calls-primary-color)"
                        />
                      </div>
                      <div className="detail-recording-info">
                        <div className="detail-recording-name">Transcript</div>
                        <div className="detail-recording-meta">
                          {t.startTime
                            ? `${formatDate(t.startTime)} · ${formatTime(t.startTime)}`
                            : (t.transcriptDate ?? 'Unknown date')}
                          {duration > 0
                            ? ` · ${formatDurationSeconds(duration)}`
                            : ''}
                        </div>
                      </div>
                      {t.transcriptUrl ? (
                        // transcriptUrl points at a downloadable transcript JSON —
                        // the record carries no utterance text itself.
                        <a
                          className="detail-transcript-link"
                          href={assetUrl(t.transcriptUrl)}
                          target="_blank"
                          rel="noreferrer">
                          Open ↗
                        </a>
                      ) : (
                        <span className="detail-transcript-link detail-transcript-link--disabled">
                          —
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="detail-no-recordings">
                No transcripts for this session
              </span>
            )}
          </div>
        </div>
        {chatGroup && (
          <Dialog
            bare
            labelledBy={`${titleId}-chat`}
            onClose={() => setChatGroup(null)}>
            <div className="detail-chat-dialog">
              <span id={`${titleId}-chat`} className="md-visually-hidden">
                Meeting chat
              </span>
              <ChatDemo group={chatGroup} onClose={() => setChatGroup(null)} />
            </div>
          </Dialog>
        )}
        {playingUrl && (
          <VideoPlayerDialog
            url={playingUrl}
            onClose={() => setPlayingUrl(null)}
          />
        )}
      </div>
    </Dialog>
  );
}

export default CallDetails;
